import type { Lesson } from './courses';

/**
 * Parcours 7 : Customer Success — 6 lecons.
 */
export const CUSTOMER_SUCCESS_LESSONS: Lesson[] = [
  {
    slug: 'cs-basics',
    title: "Qu'est-ce que le Customer Success ?",
    duration: '4 min',
    glossaryTerms: ['feedback-inbox', 'churn', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le Customer Success (succes client) est une approche proactive visant a s'assurer que vos clients atteignent leurs objectifs en utilisant votre produit. Contrairement au support client (reactif — on repond aux problemes), le Customer Success anticipe les besoins et accompagne les clients vers la reussite.\n\nLes 3 piliers du Customer Success :\n- **Onboarding** — Aider le client a demarrer rapidement et efficacement\n- **Adoption** — S'assurer que le client utilise les fonctionnalites cles\n- **Expansion** — Aider le client a tirer encore plus de valeur (upsell, cross-sell)",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "En SaaS, 70 a 80% du revenu vient des clients existants (renouvellements + expansions). Perdre un client (churn) ne coute pas seulement un abonnement : c'est aussi le cout d'acquisition gaspille et le revenu futur perdu.\n\nLes entreprises qui investissent dans le Customer Success reduisent leur churn de 30 a 50% et augmentent leur revenu par client de 20 a 30% (Gainsight).",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definissez le \"succes\" pour vos clients** — Quels resultats concrets veulent-ils atteindre ?\n2. **Creez un parcours d'onboarding** — Les 30 premiers jours sont critiques\n3. **Identifiez les signaux d'alerte** — Baisse d'utilisation, tickets de support frequents\n4. **Mesurez la satisfaction** — NPS, CSAT, analyse de sentiment des feedbacks\n5. **Agissez proactivement** — Contactez les clients a risque avant qu'ils ne churent",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Feedback Inbox** centralise tous les retours de vos clients avec analyse IA automatique du sentiment et de la priorite. C'est votre outil principal pour ecouter vos clients et detecter les signaux d'alerte avant qu'il ne soit trop tard.",
      },
    ],
    linkedModule: {
      label: 'Feedback Inbox',
      path: '/feedback-inbox',
      icon: 'Heart',
    },
  },
  {
    slug: 'onboarding-clients',
    title: "L'onboarding client parfait",
    duration: '4 min',
    glossaryTerms: ['feedback-inbox', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "L'onboarding client est le processus qui guide un nouveau client de l'inscription jusqu'a sa premiere valeur percue (le \"Aha moment\"). C'est la phase la plus critique du cycle de vie client : un onboarding rate est la premiere cause de churn precoce.\n\nUn bon onboarding doit etre :\n- **Rapide** — Le client doit percevoir de la valeur en quelques minutes, pas en quelques jours\n- **Guide** — Des etapes claires, pas un dashboard vide\n- **Personnalise** — Adapte au role et aux objectifs du client\n- **Mesure** — Vous devez savoir ou chaque client en est dans son parcours",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "63% des clients considerent que l'onboarding est un facteur cle dans leur decision de continuer a utiliser un produit (Wyzowl). Les clients qui completent l'onboarding ont un taux de retention 3x superieur a ceux qui l'abandonnent en cours de route.\n\nRule of thumb : si votre client n'a pas atteint son \"Aha moment\" dans les 7 premiers jours, la probabilite de churn augmente significativement.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Identifiez le \"Aha moment\"** — Quelle est la premiere action qui fait dire au client \"c'est genial\" ?\n2. **Creez un parcours en etapes** — 5-7 etapes maximum vers le \"Aha moment\"\n3. **Guidez visuellement** — Checklist, barre de progression, tooltips contextuels\n4. **Envoyez des emails de suivi** — J+1, J+3, J+7 avec des conseils personnalises\n5. **Mesurez le taux de completion** — Quel pourcentage de nouveaux clients complete l'onboarding ?",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le centre d'apprentissage que vous utilisez en ce moment est un exemple d'onboarding par l'education. Le **Feedback Inbox** vous permet de collecter les retours de vos propres clients pendant leur onboarding pour l'ameliorer continuellement.",
      },
    ],
    linkedModule: {
      label: 'Feedback Inbox',
      path: '/feedback-inbox',
      icon: 'Heart',
    },
  },
  {
    slug: 'feedback-loops',
    title: 'Les boucles de feedback',
    duration: '3 min',
    glossaryTerms: ['feedback-inbox', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Une boucle de feedback (Feedback Loop) est un processus cyclique : vous collectez les retours clients, les analysez, agissez dessus, puis communiquez les changements aux clients. C'est le moteur de l'amelioration continue produit.\n\nLe cycle :\n- **Capturer** — Collectez les feedbacks de tous les canaux (in-app, email, support, appels)\n- **Analyser** — Triez, categorisez, priorisez (manuellement ou via IA)\n- **Agir** — Implementez les ameliorations ou corrigez les problemes\n- **Communiquer** — Informez les clients que leur feedback a ete pris en compte",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Le NPS (Net Promoter Score) des entreprises qui ferment la boucle de feedback (qui communiquent les actions prises) est en moyenne 20 points superieur a celles qui ne le font pas.\n\nUn client qui voit son feedback pris en compte devient un promoteur. Un client dont le feedback est ignore devient un detracteur. La difference entre les deux : une simple communication.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Centralisez vos feedbacks** — Un seul outil pour tous les canaux\n2. **Categorisez automatiquement** — Bug, feature request, plainte, compliment\n3. **Priorisez avec l'IA** — Sentiment, frequence, impact business\n4. **Creez un backlog feedback** — Alimentez votre roadmap produit avec les retours clients\n5. **Fermez la boucle** — Envoyez un message au client quand son feedback est traite",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Feedback Inbox** est concu pour fermer la boucle : chaque feedback est automatiquement analyse par l'IA (sentiment, priorite, categorie), puis peut etre converti en ticket ou action. Vous gardez la trace de chaque retour et pouvez communiquer les resultats aux clients.",
      },
    ],
    linkedModule: {
      label: 'Feedback Inbox',
      path: '/feedback-inbox',
      icon: 'Heart',
    },
  },
  {
    slug: 'retention',
    title: 'Fideliser et reduire le churn',
    duration: '4 min',
    glossaryTerms: ['churn', 'feedback-inbox', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "La retention mesure votre capacite a garder vos clients dans le temps. Le churn (taux d'attrition) est son inverse : le pourcentage de clients qui se desabonnent sur une periode donnee.\n\nTypes de churn :\n- **Churn volontaire** — Le client decide de partir (insatisfaction, concurrent, budget)\n- **Churn involontaire** — Probleme technique (carte expiree, echec paiement)\n- **Churn passif** — Le client cesse d'utiliser le produit sans se desabonner formellement",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Un taux de churn de 5% par mois signifie que vous perdez la moitie de vos clients en un an. Meme avec une acquisition forte, un churn eleve rend la croissance impossible — c'est comme remplir un seau perce.\n\nReduire le churn de seulement 1% peut augmenter la valorisation d'un SaaS de 12% (Bessemer Venture Partners). La retention est le KPI le plus important en SaaS.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Mesurez le churn mensuellement** — Nombre de desabonnements / nombre total de clients en debut de mois\n2. **Analysez les raisons** — Sondez les clients qui partent (exit survey)\n3. **Identifiez les signaux precurseurs** — Baisse d'utilisation, tickets en hausse, absence de connexion\n4. **Creez des alertes** — Notification quand un client montre des signes de desengagement\n5. **Agissez proactivement** — Contactez les clients a risque avec une offre d'aide ou un incentive",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Feedback Inbox** avec son analyse de sentiment IA vous permet de detecter les clients insatisfaits avant qu'ils ne churent. Un feedback avec un sentiment negatif et une haute priorite est un signal d'alerte a traiter immediatement.",
      },
    ],
    linkedModule: {
      label: 'Feedback Inbox',
      path: '/feedback-inbox',
      icon: 'Heart',
    },
  },
  {
    slug: 'support-ai',
    title: "Le support augmente par l'IA",
    duration: '3 min',
    glossaryTerms: ['feedback-inbox', 'knowledge-explorer', 'rag'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le support augmente par l'IA combine l'expertise humaine avec l'intelligence artificielle pour offrir des reponses plus rapides et plus pertinentes. L'IA ne remplace pas l'humain : elle l'assiste en suggerant des reponses, en categorisant les tickets et en detectant les patterns.\n\nNiveaux d'assistance IA :\n- **Categorisation automatique** — L'IA trie les tickets par type, priorite et sentiment\n- **Suggestions de reponse** — L'IA propose des reponses basees sur la Knowledge Base\n- **Detection de patterns** — L'IA identifie les problemes recurrents a resoudre a la racine",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Le temps de premiere reponse est le facteur numero 1 de satisfaction support. Les clients attendent une reponse en moins d'1 heure pour les requetes urgentes. L'IA permet de reduire ce temps de 60 a 80% en suggerant instantanement des reponses pertinentes.\n\nLes equipes support qui utilisent l'IA traitent en moyenne 3x plus de tickets par agent, tout en maintenant un CSAT (Customer Satisfaction Score) superieur.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Alimentez votre Knowledge Base** — Plus elle est riche, meilleures seront les suggestions IA\n2. **Activez la categorisation automatique** — Gagnez du temps sur le tri des tickets\n3. **Utilisez les suggestions de reponse** — L'IA propose, vous validez et personnalisez\n4. **Analysez les patterns** — Identifiez les problemes recurrents pour les resoudre a la source\n5. **Mesurez le CSAT** — Assurez-vous que la qualite ne baisse pas avec l'acceleration",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Feedback Inbox** analyse automatiquement chaque feedback avec l'IA (Claude). Le **Knowledge Explorer** alimente la base de connaissances qui permet a l'IA de generer des suggestions de reponse contextualisees.",
      },
    ],
    linkedModule: {
      label: 'Knowledge Explorer',
      path: '/growth/knowledge-explorer',
      icon: 'BookOpen',
    },
  },
  {
    slug: 'your-cs-tools',
    title: 'Feedback Inbox & Analytics dans FO',
    duration: '3 min',
    glossaryTerms: ['feedback-inbox', 'marketing-dashboard', 'knowledge-explorer'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Feedback Orchestrator fournit deux outils complementaires pour le Customer Success :\n\n- **Feedback Inbox** — Centralisation et analyse IA de tous les retours clients (sentiment, priorite, categorie)\n- **Marketing Dashboard** — Metriques de performance et vision d'ensemble des indicateurs clients\n\nCombines avec le **Knowledge Explorer** pour alimenter l'IA, ces outils forment un ecosysteme complet pour ecouter, comprendre et agir sur les retours clients.",
      },
      {
        type: 'methodology',
        title: "Comment les utiliser ?",
        content:
          "1. **Centralisez les feedbacks** — Configurez la collecte multi-canal dans le Feedback Inbox\n2. **Analysez les sentiments** — L'IA detecte automatiquement le ton positif, neutre ou negatif\n3. **Priorisez les actions** — Traitez les feedbacks critiques en premier\n4. **Alimentez la Knowledge Base** — Ajoutez les FAQ et solutions dans le Knowledge Explorer\n5. **Suivez les tendances** — Le Marketing Dashboard montre l'evolution de la satisfaction dans le temps\n6. **Fermez les boucles** — Informez les clients que leurs retours ont ete pris en compte",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Rendez-vous sur le **Feedback Inbox** pour voir tous vos retours clients en un coup d'oeil. Chaque feedback est enrichi par l'IA : sentiment, priorite, categorie. Le **Marketing Dashboard** complete la vision avec les metriques globales de satisfaction et engagement.",
      },
    ],
    linkedModule: {
      label: 'Feedback Inbox',
      path: '/feedback-inbox',
      icon: 'Heart',
    },
  },
];
